// src/services/mealPlans.ts
//
// Meal-plans-domain service. Covers the full meal plan lifecycle: list, create,
// update, duplicate, delete, suggest-from-template, suggest-from-assigned-client,
// dish autocomplete + details, plan-count summary, and the send-to-client
// approval flow.

import { apiFetch } from "../Component/api/apiFetch";
import { API_URLS } from "../Component/api/url";
import { handleJson } from "./http";

export const getMealPlanDetails = (userId: string | number): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.GET_MEAL_PLAN_DETAILS(userId), { method: "GET" })
  );

export const addMealPlanDetails = (
  payload: Record<string, any>
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.ADD_MEAL_PLAN_DETAILS, {
      method: "POST",
      body: JSON.stringify(payload),
    })
  );

// Backend accepts POST with a filter body:
//   Required: page, page_size, target_goal
//   Optional: cal_min, cal_max, protein_min, protein_max, health_goal, category_id,
//   meal_plan_type, meals_per_day, options_per_meal, nutritionist_ids, search.
export const getGlobalTemplatesSuggest = (
  userId: string | number,
  filters: Record<string, any> = {}
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.GLOBLE_TEMPLATES_SUGGEST(userId), {
      method: "POST",
      body: JSON.stringify(filters),
    })
  );

export const getGlobalTemplateDetails = (
  templateId: string | number
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.GET_GLOBAL_TEMPLATE_DETAILS(templateId), {
      method: "GET",
    })
  );

// Backend accepts POST with a filter body:
//   Required: page, page_size, target_goal
//   Optional: filter_user_id (from GET_CLIENT_LIST_FOR_MEAL_PLAN),
//   cal_min, cal_max, protein_min, protein_max, nutritionist_ids,
//   meal_plan_type, meals_per_day, options_per_meal, search, exclude_user_id.
export const getMealPlanSuggest = (
  userId: string | number,
  filters: Record<string, any> = {}
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.MEAL_PLAN_SUGGEST(userId), {
      method: "POST",
      body: JSON.stringify(filters),
    })
  );

// Client list feeding the "Select client" dropdown in the Copy-from-client tab.
// Supports optional server-side search on name/phone.
// Returns { success, data: [{ user_id, name, phone }] }.
export const getClientListForMealPlan = (search: string = ""): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.GET_CLIENT_LIST_FOR_MEAL_PLAN(search), { method: "GET" })
  );

// Filter global templates by nutrition ranges (cal/protein/carbs/fat/gl min-max).
// Also accepts page, page_size, target_goal, category_id, meal_plan_type,
// meals_per_day, options_per_meal, nutritionist_ids, search in the body.
export const getMealPlanFilterTemplate = (
  userId: string | number,
  filters: Record<string, any> = {}
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.MEAL_PLAN_FILTER_TEMPLATE(userId), {
      method: "POST",
      body: JSON.stringify(filters),
    })
  );

// Filter assigned-client meal plans by nutrition ranges (cal/protein/carbs/fat/gl min-max).
// Also accepts page, page_size, target_goal, filter_user_id, meal_plan_type,
// meals_per_day, options_per_meal, nutritionist_ids, search, exclude_user_id
// in the body.
export const getMealPlanFilterAssignedClient = (
  userId: string | number,
  filters: Record<string, any> = {}
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.MEAL_PLAN_FILTER_ASSIGNED_CLIENT(userId), {
      method: "POST",
      body: JSON.stringify(filters),
    })
  );

export const duplicateMealPlan = (
  payload: Record<string, any>
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.MEAL_PLAN_DUPLICATE, {
      method: "POST",
      body: JSON.stringify(payload),
    })
  );

export const duplicateMealPlanTemplate = (
  payload: Record<string, any>
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.MEAL_PLAN_DUPLICATE_TEMPLATE, {
      method: "POST",
      body: JSON.stringify(payload),
    })
  );

export const getAssignedClientMealPlanPreview = (
  mealPlanId: string | number
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.COPY_FROM_ASSIGNED_CLIENT_MEAL_PLAN_PREVIEW(mealPlanId), {
      method: "GET",
    })
  );

export const getMealPlanCount = (
  userId: string | number,
  calMin: string | number = "",
  calMax: string | number = "",
  proteinMin: string | number = "",
  proteinMax: string | number = ""
): Promise<any> =>
  handleJson(
    apiFetch(
      API_URLS.GET_MEAL_PLAN_COUNT(userId, calMin, calMax, proteinMin, proteinMax),
      { method: "GET" }
    )
  );

export const updateMealPlanDetails = (
  payload: Record<string, any>
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.UPDATE_MEAL_PLAN_DETAILS, {
      method: "POST",
      body: JSON.stringify(payload),
    })
  );

export const getMealPlanDetail = (
  payload: Record<string, any>
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.GET_MEAL_PLAN_DETAIL, {
      method: "POST",
      body: JSON.stringify(payload),
    })
  );

export const getAllDishesMealPlan = (
  userId: string | number,
  search: string = ""
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.GET_ALL_DISHES_MEAL_PLAN(userId, search), {
      method: "GET",
    })
  );

export const autocompleteDishName = (search: string = ""): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.AUTOCOMPLETE_DISH_NAME(search), { method: "GET" })
  );

export const getDishDetails = (dishId: string | number): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.GET_DISH_MEASUREMENT_LIST(dishId), { method: "GET" })
  );

export const createMealPlan = (payload: Record<string, any>): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.CREATE_MEAL_PLAN, {
      method: "POST",
      body: JSON.stringify(payload),
    })
  );

export const deleteMealCardPlan = (
  payload: Record<string, any>
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.DELETE_MEAL_CARD_PLAN, {
      method: "POST",
      body: JSON.stringify(payload),
    })
  );

export const deleteMealPlan = (payload: Record<string, any>): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.DELETE_MEAL_PLAN, {
    method: "DELETE",
      body: JSON.stringify(payload),
    })
  );

export const updateMealPlan = (payload: Record<string, any>): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.UPDATE_MEAL_PLAN, {
      method: "POST",
      body: JSON.stringify(payload),
    })
  );

export const sendMealPlanToClient = (
  payload: Record<string, any>
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.MEAL_PLAN_SEND_TO_CLIENT, {
      method: "POST",
      body: JSON.stringify(payload),
    })
  );

export const getDuplicateMealPlanDetails = (
  payload: Record<string, any>
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.DUPLICATE_MEAL_PLAN, {
      method: "POST",
      body: JSON.stringify(payload),
    })
  );

export const getDuplicateMealOptionDetails = (
  payload: Record<string, any>
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.DUPLICATE_MEAL_OPTION, {
      method: "POST",
      body: JSON.stringify(payload),
    })
  );

export const getMealPlanTypeList = (): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.GET_MEAL_PLAN_TYPE_LIST, { method: "GET" })
  );
